import { useEffect, useState } from 'react'
import { PlanIntervalSwitcher } from '@/components/subscription/PlanIntervalSwitcher'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useListPlanOptions } from '@/stores/billing.store'
import type { BillingIntervalEnum, OrganizationBillingState } from '@/gen/cortado/v1/subscriptions_pb'
import { CheckoutRecoveryPanel } from './CheckoutRecoveryPanel'
import { InlineCheckoutPanel } from './InlineCheckoutPanel'
import type { LiveCheckoutTotals } from './order-summary-model'
import { OrderSummary } from './OrderSummary'
import { PlanOptionsUnavailable } from './PlanOptionsUnavailable'
import { useCheckoutFlow, type CheckoutStep } from './use-checkout-flow'

export interface CheckoutFlowProps {
  organizationId: string
  billingState: OrganizationBillingState
  onSuccess: () => void
  checkoutReturnPath?: string
  onPending?: (checkoutId: string) => void
  // When set, the matching interval is pre-selected once plan options load.
  defaultBillingInterval?: BillingIntervalEnum
  // 'page' is the two-column /start-trial layout. 'compact' stays single
  // column at every width for the ~700px onboarding and settings containers.
  density?: 'page' | 'compact'
}

const CHECKOUT_STEPS: ReadonlyArray<{ value: CheckoutStep; label: string }> = [
  { value: 'plan', label: 'Plan' },
  { value: 'payment', label: 'Payment' },
  { value: 'activating', label: 'Activating' },
]

function StepHeader({ step }: { step: CheckoutStep }) {
  return (
    <ol className="flex items-center gap-3 text-sm" aria-label="Checkout steps">
      {CHECKOUT_STEPS.map(({ value, label }, index) => (
        <li key={value} className="flex items-center gap-3">
          {index > 0 ? <span aria-hidden="true" className="text-muted-foreground">·</span> : null}
          <span
            aria-current={step === value ? 'step' : undefined}
            className={cn(
              'flex items-center gap-2',
              step === value ? 'font-semibold text-foreground' : 'text-muted-foreground'
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full border text-xs',
                step === value ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
              )}
            >
              {index + 1}
            </span>
            {label}
          </span>
        </li>
      ))}
    </ol>
  )
}

/**
 * Inline, Shopify-style subscription checkout: the main column steps from plan
 * selection to the custom Stripe payment form while the order summary stays
 * on screen. Shared by /start-trial, the onboarding subscribe step, and the
 * billing settings card so none of them has to redirect to another.
 */
export function CheckoutFlow({
  organizationId,
  billingState,
  onSuccess,
  checkoutReturnPath,
  onPending,
  defaultBillingInterval,
  density = 'page',
}: CheckoutFlowProps) {
  const {
    planOptions,
    hasLoaded: plansLoaded,
    isError: plansErrored,
  } = useListPlanOptions(organizationId)
  const flow = useCheckoutFlow({ organizationId, onSuccess, onPending })
  const [liveTotals, setLiveTotals] = useState<LiveCheckoutTotals | null>(null)

  // Live totals belong to a mounted Checkout Session. Returning to plan drops
  // them back to the quoted figures; activation keeps the completed totals.
  useEffect(() => {
    if (flow.step === 'plan') setLiveTotals(null)
  }, [flow.step])

  useEffect(() => {
    if (!defaultBillingInterval || flow.selection || !plansLoaded || planOptions.length === 0) {
      return
    }

    const planKeys = [...new Set(planOptions.map((option) => option.planKey))]
    const effectivePlanKey = billingState.assignedPlanKey || planKeys[0]
    const option = planOptions.find(
      (candidate) =>
        candidate.planKey === effectivePlanKey &&
        candidate.billingInterval === defaultBillingInterval
    )
    if (option) {
      flow.selectPlan({
        planKey: option.planKey,
        billingInterval: option.billingInterval,
        option,
      })
    }
  }, [
    billingState.assignedPlanKey,
    defaultBillingInterval,
    flow.selection,
    flow.selectPlan,
    planOptions,
    plansLoaded,
  ])

  // Once ListPlanOptions has resolved (or errored) with nothing to show,
  // there is no interval left to promise or pick - never render the picker
  // over emptiness. A selection already in hand carries its own PlanOption,
  // so a later list failure (e.g. a refetch) no longer applies here.
  const plansEmpty = plansLoaded && planOptions.length === 0
  const plansUnavailable = !flow.selection && (plansErrored || plansEmpty)
  const showSteps = !flow.pendingCheckoutId && !plansUnavailable

  const main = flow.pendingCheckoutId ? (
    <CheckoutRecoveryPanel
      checkoutId={flow.pendingCheckoutId}
      exhausted={flow.phase === 'recovering'}
      retryCount={flow.retryCount}
      onRetry={flow.retryConfirmation}
      onStartOver={flow.startOver}
    />
  ) : plansUnavailable ? (
    <PlanOptionsUnavailable
      variant={plansErrored ? 'error' : 'empty'}
      onRetry={() => window.location.reload()}
    />
  ) : flow.step === 'plan' ? (
    <div className="space-y-5">
      <p className="text-muted-foreground">
        {billingState.trialAvailable
          ? 'Choose the billing interval you want after the trial. You can cancel before the first charge.'
          : 'Choose your billing interval. Your subscription starts today, and you can cancel anytime.'}
      </p>
      <PlanIntervalSwitcher
        organizationId={organizationId}
        billingState={billingState}
        mode="subscribe"
        variant={density === 'compact' ? 'compact' : 'default'}
        highlightedPlanKey={flow.selection?.planKey}
        highlightedBillingInterval={flow.selection?.billingInterval}
        onSubscribe={(planKey, billingInterval, option) =>
          flow.selectPlan({ planKey, billingInterval, option })
        }
      />
      {flow.selection && (
        <Button className="w-full sm:w-auto" onClick={flow.continueToPayment}>
          {billingState.trialAvailable ? 'Continue to free trial' : 'Continue to payment'}
        </Button>
      )}
    </div>
  ) : flow.selection ? (
    // Deliberately not keyed by selection: the panel's own generation
    // guard handles a changed selection, and a changing key would
    // remount it into a duplicate CreateCheckoutSession POST.
    <InlineCheckoutPanel
      organizationId={organizationId}
      planKey={flow.selection.planKey}
      billingInterval={flow.selection.billingInterval}
      checkoutReturnPath={checkoutReturnPath}
      trialAvailable={billingState.trialAvailable}
      onClose={flow.backToPlan}
      onTotalsChange={setLiveTotals}
      onActivationStart={flow.beginActivation}
      onPending={flow.markPending}
      onSuccess={flow.completeCheckout}
    />
  ) : null

  const pageContentRow = showSteps ? 'lg:row-start-2' : 'lg:row-start-1'
  const orderSummary = (
    <OrderSummary
      billingState={billingState}
      selection={flow.selection}
      collapsible={density === 'page'}
      liveTotals={liveTotals}
    />
  )

  return (
    <section
      aria-label="Subscription checkout"
      className={cn(
        density === 'page'
          ? 'grid items-start gap-x-6 gap-y-5 lg:grid-cols-[minmax(0,1fr)_360px]'
          : 'flex flex-col gap-6'
      )}
    >
      {density === 'page' && showSteps ? (
        <div className="lg:col-span-2 lg:row-start-1">
          <StepHeader step={flow.step} />
        </div>
      ) : null}

      {density === 'compact' ? (
        <>
          <div className={cn('min-w-0', showSteps && 'space-y-5')}>
            {showSteps ? <StepHeader step={flow.step} /> : null}
            {main}
          </div>
          <div>{orderSummary}</div>
        </>
      ) : (
        <>
          {/* On the full-page checkout, the summary stays ahead of the form
              on narrow screens. At desktop widths both cards share one
              explicit grid row beneath the steps. */}
          <div className={cn('lg:sticky lg:top-6 lg:col-start-2', pageContentRow)}>
            {orderSummary}
          </div>
          <div className={cn('min-w-0 lg:col-start-1', pageContentRow)}>
            {main}
          </div>
        </>
      )}
    </section>
  )
}
